import { dayjs } from "@/core/utils/date";
import { packChatPlainText } from "./message-preview";
import { describeReference } from "./reference-label";
import type {
  IPackChatTechnicianGroup,
  IPackChatThreadSummary,
  IPackChatThreadOverview,
  IPackChatThreadsResponse,
  PackChatStatus,
} from "./types";
import { PACK_CHAT_STATUS } from "./types";

const newestFirst = (a: IPackChatThreadSummary, b: IPackChatThreadSummary) =>
  dayjs(b.updated_at).valueOf() - dayjs(a.updated_at).valueOf();

/** Preserve server priority within each technician and order groups by first occurrence. */
export const groupThreadsByTechnician = (
  threads: IPackChatThreadSummary[],
): IPackChatTechnicianGroup[] => {
  const byTechnician = new Map<string, IPackChatThreadSummary[]>();

  threads.forEach((thread) => {
    const existing = byTechnician.get(thread.technician.id);
    if (existing) existing.push(thread);
    else byTechnician.set(thread.technician.id, [thread]);
  });

  return Array.from(byTechnician.values()).map((group) => {
    const open = group.filter((thread) => thread.status === "open");
    const mostRecent = [...group].sort(newestFirst)[0];

    return {
      technician: group[0].technician,
      threads: group,
      openCount: open.length,
      hasAiMessages: group.some((thread) => thread.has_ai_messages),
      needsManager: group.some((thread) => thread.needs_manager),
      // One row covers several conversations, so AI only speaks for this
      // technician while it owns every open one. A single conversation a
      // manager has taken over puts a person back on the row.
      responseOwner:
        open.length > 0 &&
        open.every((thread) => thread.response_owner === "ai")
          ? ("ai" as const)
          : ("manager" as const),
      lastUpdatedAt: mostRecent.updated_at,
      lastMessageThread: mostRecent,
    };
  });
};

/** Open the first conversation in server order, including closed history. */
export const packChatFirstThreadId = (
  threads: IPackChatThreadSummary[],
): string | null => threads[0]?.id ?? null;

const matchesSearch = (thread: IPackChatThreadSummary, search: string) => {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;

  return [
    thread.technician.name,
    describeReference(thread.context),
    thread.last_message && packChatPlainText(thread.last_message.body),
  ]
    .filter((field): field is string => Boolean(field))
    .some((field) => field.toLowerCase().includes(needle));
};

/**
 * The whole shape the PackChat tab reads, derived from one list response.
 *
 * A `select` for the list query rather than work in the component: the endpoint
 * returns a flat array of threads and every reader of it wants the same two
 * things — counts across the period, and those threads collapsed into people.
 * Deriving here means the component holds filter state and nothing else, and the
 * shaping is memoised by react-query against the cached response.
 *
 * Counts deliberately ignore the filters: they label the filter control itself,
 * so filtering them would leave one number true and hide the other two.
 */
export const selectPackChatTechnicianGroups =
  (filters?: { status?: PackChatStatus; search?: string }) =>
  (data: IPackChatThreadsResponse): IPackChatThreadOverview => {
    const threads = data.threads;

    return {
      counts: {
        all: threads.length,
        open: threads.filter(
          (thread) => thread.status === PACK_CHAT_STATUS.open,
        ).length,
        closed: threads.filter(
          (thread) => thread.status === PACK_CHAT_STATUS.closed,
        ).length,
      },
      groups: groupThreadsByTechnician(
        threads.filter(
          (thread) =>
            (!filters?.status || thread.status === filters.status) &&
            matchesSearch(thread, filters?.search ?? ""),
        ),
      ),
    };
  };
